import React, { useState, useEffect, useMemo } from 'react';
import {
  Card, Row, Col, Input, InputNumber, Button, message, Tag, Space, Table, Modal, Descriptions,
  Popconfirm, Spin, Alert, Divider, Tooltip, Segmented, Select,
} from 'antd';
import {
  PlusOutlined, SaveOutlined, DeleteOutlined, EyeOutlined, CalculatorOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import { apiFetch } from '../utils/api.js';
import { yuan, zoneTagColor } from '../utils/format.js';
import { useAuth } from '../auth.jsx';

// 智能数字：保留原始有效数字（"0.148" 不变成 "0.15"，"0.5" 不变成 "0.50"），同时去除 IEEE 754 浮点小尾巴
const smart = (v) => {
  if (v == null || isNaN(v)) return '-';
  return Number.parseFloat(Number(v).toPrecision(12)).toString();
};

const ZONES = ['一区', '二区', '三区']; // 四区基本不做，盈亏计算只做三区

// UTC 时间戳（后端 toISOString）转北京时间显示
function fmtTime(s) {
  if (!s) return '-';
  const d = new Date(s.replace(' ', 'T') + 'Z');
  if (isNaN(d)) return s;
  const bj = new Date(d.getTime() + 8 * 3600 * 1000);
  const p = n => String(n).padStart(2, '0');
  return `${bj.getFullYear()}-${p(bj.getMonth() + 1)}-${p(bj.getDate())} ${p(bj.getHours())}:${p(bj.getMinutes())}`;
}

// 空规格
const emptySpec = () => ({ key: Date.now() + Math.random(), qty: null, price: null, ratio: 0, freight: { '一区': null, '二区': null, '三区': null } });

// 模块级表单缓存：切换页面时组件卸载但 JS 内存不丢 → 值保留；手动刷新/关闭页面时整页重载 → 缓存清空归零
let _formCache = null;

// 空表单（归零后的初始状态，不再写死示例默认值）
const emptyState = () => ({
  name: '',
  perSetCost: null,
  otherCost: null,
  serviceRate: null,
  returnRate: null,
  onlyRefundRate: null,
  bid: null,
  roiBid: null,
  bidMode: 'cpa',
  zoneRatios: { '一区': null, '二区': null, '三区': null },
  specs: [emptySpec()],
});

export default function ProfitCalc() {
  const { user } = useAuth();
  const isGuest = (user && user.role === 'guest');
  const init = _formCache || emptyState();
  // 商品信息
  const [name, setName] = useState(init.name);
  // 全局参数
  const [perSetCost, setPerSetCost] = useState(init.perSetCost); // 每套成本（元）
  const [otherCost, setOtherCost] = useState(init.otherCost);      // 其他单票直接成本（元）
  const [serviceRate, setServiceRate] = useState(init.serviceRate);  // 技术服务费率（%）
  const [returnRate, setReturnRate] = useState(init.returnRate);       // 退货率（%）
  const [onlyRefundRate, setOnlyRefundRate] = useState(init.onlyRefundRate); // 仅退款率（%）
  const [bid, setBid] = useState(init.bid);                     // 出价（每笔成交推广费，元/单）
  const [roiBid, setRoiBid] = useState(init.roiBid);               // 目标投产比（投产比出价模式）
  const [bidMode, setBidMode] = useState(init.bidMode);         // 出价模式：cpa=成交出价 / roi=投产比出价
  // 地区占比（%）
  const [zoneRatios, setZoneRatios] = useState(init.zoneRatios);
  // 规格列表
  const [specs, setSpecs] = useState(init.specs);
  // 已保存列表
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // 历史订单填充：产品列表 + 选中编码 + 填充中
  const [historyProducts, setHistoryProducts] = useState([]);
  const [historyCode, setHistoryCode] = useState(null);
  const [filling, setFilling] = useState(false);
  // 详情 Modal
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);

  // 每次渲染后把表单状态写回模块缓存：切换页面(卸载)后下次进入能恢复；整页刷新则内存清空、归零
  useEffect(() => {
    _formCache = { name, perSetCost, otherCost, serviceRate, returnRate, onlyRefundRate, bid, roiBid, bidMode, zoneRatios, specs };
  });

  const loadSaved = () => {
    apiFetch('/api/profit-calc')
      .then(r => r.json())
      .then(d => { setSaved(d.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  };
  useEffect(() => { loadSaved(); }, []);

  // 加载可填充的历史产品列表（已卖过、有发货记录的 spec_code）
  useEffect(() => {
    apiFetch('/api/profit-calc/region-stats')
      .then(r => r.json())
      .then(d => setHistoryProducts(d.data || []))
      .catch(() => {});
  }, []);

  // ---------- 详情弹窗 ----------
  const openDetail = async (id) => {
    try {
      const res = await apiFetch('/api/profit-calc/' + id).then(r => r.json());
      // 后端 GET 接口不带 success 字段，只检查 data
      if (!res.data) { message.error('加载失败：记录不存在或接口异常'); return; }
      setDetailData(res.data.data || {});
      setDetailOpen(true);
    } catch (e) { console.error('openDetail 失败:', e); message.error('加载失败：' + (e?.message || '网络异常')); }
  };

  // 用详情数据重算明细（保存时只存了 summary，明细按公式实时重算）
  const detailComputed = useMemo(() => {
    if (!detailData) return null;
    const d = detailData;
    const svcRate = d.serviceRate ?? 0.007;
    const retRate = d.returnRate ?? 0.04;
    const refundRate = d.onlyRefundRate ?? 0.02;
    const perSet = d.perSetCost ?? 0.148;
    const other = d.otherCost ?? 0.5;
    const bidVal = d.bid ?? 4;
    const roiVal = d.roiBid ?? 0;
    const isRoi = d.bidMode === 'roi' && roiVal > 0;
    const zr = {};
    ZONES.forEach(z => { zr[z] = d.zoneRatios?.[z] ?? 0; });
    let wMargin = 0, wPrice = 0, wMarginNoBid = 0, wAd = 0, wFreight = 0;
    const detail = [];
    (d.specs || []).forEach((s, i) => {
      const cost = perSet * (s.qty ?? 0);
      const svcFee = (s.price ?? 0) * svcRate * (1 - retRate - refundRate);
      const onlyRefundLoss = (s.price ?? 0) * refundRate;
      const ratio = s.ratio ?? 0;
      ZONES.forEach(z => {
        const freight = s.freight?.[z] ?? 0;
        const retLoss = ((s.price ?? 0) - cost) * retRate;
        const adCost = isRoi ? ((s.price ?? 0) / roiVal) : bidVal;
        const gp = (s.price ?? 0) - cost - freight - other - svcFee - retLoss - onlyRefundLoss - adCost;
        const margin = s.price ? gp / s.price : 0;
        const weight = ratio * zr[z];
        wMargin += gp * weight;
        wMarginNoBid += (gp + adCost) * weight;
        wPrice += (s.price ?? 0) * weight;
        wAd += adCost * weight;
        wFreight += freight * weight;
        detail.push({
          key: `${i}-${z}`, spec: `${s.qty ?? 0}套`, zone: z,
          price: s.price ?? 0, cost, freight, svcFee, retLoss, onlyRefundLoss, bid: adCost, gp, margin, weight,
        });
      });
    });
    const overall = wPrice ? wMargin / wPrice : 0;
    const bidRatio = isRoi ? (roiVal > 0 ? 1 / roiVal : 0) : (wPrice ? bidVal / wPrice : 0);
    const netMarginNoBid = wPrice ? wMarginNoBid / wPrice : 0;
    const breakEvenROI = netMarginNoBid > 0 ? 1 / netMarginNoBid : null;
    const currentROI = isRoi ? roiVal : (bidRatio > 0 ? 1 / bidRatio : null);
    return { detail, overall, wMargin, wPrice, bidRatio, netMarginNoBid, breakEvenROI, currentROI, avgAd: wAd, avgFreight: wFreight, isRoi, roiVal };
  }, [detailData]);

  // ---------- 计算 ----------
  const calc = useMemo(() => {
    const svcRate = (serviceRate ?? 0) / 100;
    const retRate = (returnRate ?? 0) / 100;
    const refundRate = (onlyRefundRate ?? 0) / 100;
    const bidVal = bid ?? 0;
    const roiVal = roiBid ?? 0;
    const isRoi = bidMode === 'roi' && roiVal > 0; // 投产比出价：单笔推广费 = 售价 ÷ 目标投产比
    const zr = {};
    ZONES.forEach(z => { zr[z] = (zoneRatios[z] ?? 0) / 100; });
    let wMargin = 0, wPrice = 0, wMarginNoBid = 0, wAd = 0, wFreight = 0;
    const detail = [];
    specs.forEach(s => {
      const cost = (perSetCost ?? 0) * (s.qty ?? 0);
      const svcFee = (s.price ?? 0) * svcRate * (1 - retRate - refundRate); // 服务费净额：扣退货率+仅退款率
      const onlyRefundLoss = (s.price ?? 0) * refundRate; // 仅退款损失 = 售价 × 仅退款率
      const ratio = (s.ratio ?? 0) / 100;
      ZONES.forEach(z => {
        const freight = s.freight?.[z] ?? 0;
        const retLoss = ((s.price ?? 0) - cost) * retRate; // 退货损失 = (售价 − 货品成本) × 退货率（丢整单利润，货退回成本回收，服务费已含在净额退回）
        const adCost = isRoi ? ((s.price ?? 0) / roiVal) : bidVal; // 推广费：投产比出价=售价÷目标投产比，成交出价=固定出价
        const gp = (s.price ?? 0) - cost - freight - (otherCost ?? 0) - svcFee - retLoss - onlyRefundLoss - adCost;
        const margin = s.price ? gp / s.price : 0;
        const weight = ratio * zr[z];
        wMargin += gp * weight;
        wMarginNoBid += (gp + adCost) * weight; // 不含推广费的净毛利（保本投产比用）
        wPrice += (s.price ?? 0) * weight;
        wAd += adCost * weight;
        wFreight += freight * weight;
        detail.push({
          key: `${s.key}-${z}`, spec: `${s.qty ?? 0}套`, zone: z,
          price: s.price ?? 0, cost, freight, svcFee, retLoss, onlyRefundLoss, bid: adCost, gp, margin, weight,
        });
      });
    });
    const overall = wPrice ? wMargin / wPrice : 0;
    // 付费占比：投产比出价 = 1÷目标投产比（固定）；成交出价 = 出价÷客单价
    const bidRatio = isRoi ? (roiVal > 0 ? 1 / roiVal : 0) : (wPrice ? bidVal / wPrice : 0);
    // 净毛利率（不含推广费，已含退货损失/仅退款损失/服务费摊销）→ 保本投产比 = 1 ÷ 该值（精确版）
    const netMarginNoBid = wPrice ? wMarginNoBid / wPrice : 0;
    const breakEvenROI = netMarginNoBid > 0 ? 1 / netMarginNoBid : null;
    // 当前投产比：投产比出价 = 目标投产比本身；成交出价 = 1 ÷ 付费占比
    const currentROI = isRoi ? roiVal : (bidRatio > 0 ? 1 / bidRatio : null);
    return { detail, overall, wMargin, wPrice, bidRatio, netMarginNoBid, breakEvenROI, currentROI, avgAd: wAd, avgFreight: wFreight, isRoi, roiVal };
  }, [specs, perSetCost, otherCost, serviceRate, returnRate, onlyRefundRate, zoneRatios, bid, roiBid, bidMode]);

  const zoneTotal = ZONES.reduce((s, z) => s + (zoneRatios[z] ?? 0), 0);
  const specTotal = specs.reduce((s, x) => s + (x.ratio ?? 0), 0);

  // ---------- 规格操作 ----------
  const updateSpec = (idx, key, value) => {
    setSpecs(prev => prev.map((s, i) => (i === idx ? { ...s, [key]: value } : s)));
  };
  const updateFreight = (idx, zone, value) => {
    setSpecs(prev => prev.map((s, i) => (i === idx ? { ...s, freight: { ...s.freight, [zone]: value } } : s)));
  };
  const addSpec = () => setSpecs(prev => [...prev, emptySpec()]);
  const removeSpec = (idx) => setSpecs(prev => prev.filter((_, i) => i !== idx));

  // ---------- 从历史订单自动填充地区占比 + 平均运费 ----------
  const handleFillHistory = async () => {
    if (!historyCode) { message.warning('请先选择产品'); return; }
    setFilling(true);
    try {
      const res = await apiFetch('/api/profit-calc/region-stats?parent_code=' + encodeURIComponent(historyCode)).then(r => r.json());
      const d = res.data;
      if (!d || !d.total) { message.warning('该产品暂无历史发货数据'); return; }
      // 只取一/二/三区（盈亏计算只做三区），按单量占比归一化到 100%
      const cnt = { '一区': 0, '二区': 0, '三区': 0 };
      ZONES.forEach(z => { const zz = d.zones[z]; if (zz) cnt[z] = zz.count; });
      const sum = cnt['一区'] + cnt['二区'] + cnt['三区'];
      if (sum === 0) { message.warning('该产品在一/二/三区暂无发货数据'); return; }
      const newZR = {};
      ZONES.forEach(z => { newZR[z] = Math.round(cnt[z] / sum * 100); });
      // 四舍五入误差修正，保证合计 = 100
      newZR['三区'] += 100 - (newZR['一区'] + newZR['二区'] + newZR['三区']);
      setZoneRatios(newZR);
      // 规格列表：按真实数据生成规格行（套数 + 销量占比 + 三区运费），售价留空手动填
      if (d.specs && d.specs.length) {
        // 销量占比四舍五入到整数，最后一个规格修正使合计 = 100
        const raw = d.specs.map(s => Math.round(s.ratio));
        raw[raw.length - 1] += 100 - raw.reduce((a, b) => a + b, 0);
        const newSpecs = d.specs.map((s, i) => ({
          key: Date.now() + Math.random(),
          qty: s.qty,
          price: null,
          ratio: raw[i],
          freight: {
            '一区': s.freight?.['一区'] ?? null,
            '二区': s.freight?.['二区'] ?? null,
            '三区': s.freight?.['三区'] ?? null,
          },
        }));
        setSpecs(newSpecs);
      }
      // 每套成本：带出真实值（仍可手动改）
      if (d.per_set_cost != null) setPerSetCost(d.per_set_cost);
      message.success(`已按「${d.parent_code}」${d.total} 单历史订单填充：每套成本 ¥${d.per_set_cost ?? '-'}、地区占比 ${newZR['一区']}%/${newZR['二区']}%/${newZR['三区']}%、规格 ${d.specs.length} 个（套数+销量占比+三区运费，售价请手动填）`);
    } catch { message.error('填充失败，请重试'); }
    finally { setFilling(false); }
  };

  // ---------- 保存 / 查看 / 删除 ----------
  const handleSave = async () => {
    if (!String(name).trim()) { message.warning('请先填写商品名称'); return; }
    if (specs.length === 0) { message.warning('请至少添加一个规格'); return; }
    const data = {
      name: String(name).trim(),
      perSetCost, otherCost, bid, roiBid, bidMode,
      serviceRate: serviceRate / 100,
      returnRate: returnRate / 100,
      onlyRefundRate: onlyRefundRate / 100,
      zoneRatios: (() => { const o = {}; ZONES.forEach(z => { o[z] = (zoneRatios[z] ?? 0) / 100; }); return o; })(),
      specs: specs.map(s => ({
        qty: s.qty, price: s.price, ratio: (s.ratio ?? 0) / 100,
        freight: { '一区': s.freight?.['一区'] ?? 0, '二区': s.freight?.['二区'] ?? 0, '三区': s.freight?.['三区'] ?? 0 },
      })),
      gross_margin: calc.overall,
      weighted_margin: calc.wMargin,
      weighted_price: calc.wPrice,
    };
    setSaving(true);
    try {
      const res = await apiFetch('/api/profit-calc', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: data.name, data, gross_margin: calc.overall }),
      }).then(r => r.json());
      if (res.success) { message.success(`已保存「${data.name}」`); loadSaved(); }
      else message.error(res.error || '保存失败');
    } catch { message.error('保存失败，请重试'); }
    finally { setSaving(false); }
  };

  const loadDetail = async (id) => {
    try {
      const res = await apiFetch('/api/profit-calc/' + id).then(r => r.json());
      const d = res.data?.data;
      if (!d) { message.error('加载失败'); return; }
      setName(d.name || '');
      setPerSetCost(d.perSetCost ?? 0.148);
      setOtherCost(d.otherCost ?? 0.5);
      setBid(d.bid ?? 4);
      setRoiBid(d.roiBid ?? 5);
      setBidMode(d.bidMode === 'roi' ? 'roi' : 'cpa');
      setServiceRate((d.serviceRate ?? 0.007) * 100);
      setReturnRate((d.returnRate ?? 0.04) * 100);
      setOnlyRefundRate((d.onlyRefundRate ?? 0.02) * 100);
      const zr = {};
      ZONES.forEach(z => { zr[z] = (d.zoneRatios?.[z] ?? 0) * 100; });
      setZoneRatios(zr);
      setSpecs((d.specs || []).map((s, i) => ({
        key: i + 1,
        qty: s.qty, price: s.price,
        ratio: (s.ratio ?? 0) * 100,
        freight: { '一区': s.freight?.['一区'] ?? 0, '二区': s.freight?.['二区'] ?? 0, '三区': s.freight?.['三区'] ?? 0 },
      })));
      message.success('已加载到上方，可修改后重新保存为新记录');
    } catch { message.error('加载失败'); }
  };

  const handleDelete = async (id) => {
    const res = await apiFetch('/api/profit-calc/' + id, { method: 'DELETE' }).then(r => r.json());
    if (res.success) { message.success('已删除'); loadSaved(); }
    else message.error(res.error || '删除失败');
  };

  // ---------- 表格列 ----------
  const specColumns = [
    { title: '规格套数', dataIndex: 'qty', width: 110, render: (v, _, idx) => (
      <InputNumber min={0} precision={0} style={{ width: '100%' }} value={v ?? null} placeholder="套数"
        onChange={val => updateSpec(idx, 'qty', val)} />
    )},
    { title: '售价（元/单）', dataIndex: 'price', width: 120, render: (v, _, idx) => (
      <InputNumber min={0} precision={2} style={{ width: '100%' }} value={v ?? null} placeholder="售价"
        onChange={val => updateSpec(idx, 'price', val)} />
    )},
    { title: '销量占比（%）', dataIndex: 'ratio', width: 120, render: (v, _, idx) => (
      <InputNumber min={0} max={100} precision={0} style={{ width: '100%' }} value={v ?? null} placeholder="%"
        onChange={val => updateSpec(idx, 'ratio', val)} />
    )},
    ...ZONES.map(z => ({
      title: `${z}运费（元）`, key: `freight-${z}`, width: 115, align: 'right',
      render: (_, s, idx) => (
        <InputNumber min={0} precision={2} style={{ width: '100%' }} value={s.freight?.[z] ?? null} placeholder="运费"
          onChange={val => updateFreight(idx, z, val)} />
      ),
    })),
    { title: '操作', key: 'op', width: 56, align: 'center', fixed: 'right', render: (_, __, idx) => (
      <Popconfirm title="删除该规格？" onConfirm={() => removeSpec(idx)} okText="删除" cancelText="取消">
        <Button size="small" danger icon={<DeleteOutlined />} />
      </Popconfirm>
    )},
  ];

  const detailColumns = [
    { title: '规格', dataIndex: 'spec', width: 90, fixed: 'left' },
    { title: '地区', dataIndex: 'zone', width: 70, render: v => <Tag color={zoneTagColor(v)}>{v}</Tag> },
    { title: '售价', dataIndex: 'price', width: 80, align: 'right', render: v => yuan(v) },
    { title: '成本', dataIndex: 'cost', width: 80, align: 'right', render: v => yuan(v) },
    { title: '运费', dataIndex: 'freight', width: 80, align: 'right', render: v => yuan(v) },
    { title: '技术服务费', dataIndex: 'svcFee', width: 90, align: 'right', render: v => yuan(v) },
    { title: '退货损失', dataIndex: 'retLoss', width: 80, align: 'right', render: v => yuan(v) },
    { title: '仅退款损失', dataIndex: 'onlyRefundLoss', width: 90, align: 'right', render: v => yuan(v) },
    { title: '推广费', dataIndex: 'bid', width: 80, align: 'right', render: v => yuan(v) },
    { title: '单票毛利', dataIndex: 'gp', width: 90, align: 'right',
      render: v => <span style={{ color: v >= 0 ? '#3F8600' : '#CF1322', fontWeight: 600 }}>{v >= 0 ? '+' : ''}{yuan(v)}</span> },
    { title: '毛利率', dataIndex: 'margin', width: 80, align: 'right',
      render: v => <span style={{ color: v >= 0 ? '#3F8600' : '#CF1322' }}>{(v * 100).toFixed(2)}%</span> },
    { title: '权重', dataIndex: 'weight', width: 70, align: 'right', render: v => (v * 100).toFixed(1) + '%' },
  ];

  const savedColumns = [
    { title: '商品名称', dataIndex: 'name', ellipsis: true },
    { title: '综合毛利率', dataIndex: 'gross_margin', width: 120, align: 'center',
      render: v => v == null ? '-' : (
        <span style={{ fontSize: 14, fontWeight: 700, color: v >= 0 ? '#3F8600' : '#CF1322' }}>{(v * 100).toFixed(2)}%</span>
      )},
    { title: '保存时间', dataIndex: 'created_at', width: 160, render: v => fmtTime(v) },
    { title: '操作', key: 'op', width: 120, align: 'center',
      render: (_, r) => (
        <Space size={4}>
          <Tooltip title="查看明细"><Button size="small" icon={<EyeOutlined />} onClick={() => openDetail(r.id)} /></Tooltip>
          {!isGuest && (
            <Popconfirm title="删除该记录？" onConfirm={() => handleDelete(r.id)} okText="删除" cancelText="取消">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      )},
  ];

  const profitable = calc.overall > 0;

  if (loading) return <div style={{ padding: 24, textAlign: 'center' }}><Spin size="large" /></div>;

  return (
    <div className="page-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>盈亏计算</div>
          <div style={{ color: '#8c8c8c', fontSize: 12, marginTop: 2 }}>
            多规格 × 运费分区，含技术服务费、退货损失与仅退款损失，提前测算商品是否盈利
          </div>
        </div>
        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>保存该商品</Button>
      </div>

      {/* 商品信息 + 全局参数 */}
      <Card title="① 商品信息与成本参数" size="small" style={{ marginBottom: 12 }}>
        <Row gutter={[16, 12]}>
          <Col xs={24} md={8}>
            <div style={{ color: '#8c8c8c', fontSize: 12, marginBottom: 4 }}>商品名称</div>
            <Input placeholder="如 三边封秸秆香蕉梳" value={name} onChange={e => setName(e.target.value)} />
            <div style={{ marginTop: 8 }}>
              <Select
                showSearch
                allowClear
                placeholder="选产品（按销量排序）"
                style={{ width: '100%', marginBottom: 8 }}
                value={historyCode}
                onChange={setHistoryCode}
                options={historyProducts.map(p => ({ value: p.code, label: `${p.code}（${p.product_name}，${p.order_count}单${p.spec_count > 1 ? `，${p.spec_count}规格` : ''}）` }))}
                filterOption={(input, opt) => String(opt?.label || '').toLowerCase().includes(String(input).toLowerCase())}
              />
              <Button type="primary" ghost size="small" icon={<ThunderboltOutlined />} loading={filling} onClick={handleFillHistory} block>填充真实数据</Button>
            </div>
          </Col>
          <Col xs={12} md={4}>
            <div style={{ color: '#8c8c8c', fontSize: 12, marginBottom: 4 }}>每套成本（元）</div>
            <InputNumber min={0} precision={4} style={{ width: '100%' }} value={perSetCost} onChange={v => setPerSetCost(v ?? 0)} />
          </Col>
          <Col xs={12} md={4}>
            <div style={{ color: '#8c8c8c', fontSize: 12, marginBottom: 4 }}>其他单票直接成本（元）</div>
            <InputNumber min={0} precision={2} style={{ width: '100%' }} value={otherCost} onChange={v => setOtherCost(v ?? 0)} />
          </Col>
          <Col xs={12} md={4}>
            <div style={{ color: '#8c8c8c', fontSize: 12, marginBottom: 4 }}>技术服务费率（%）</div>
            <InputNumber min={0} precision={2} style={{ width: '100%' }} value={serviceRate} onChange={v => setServiceRate(v ?? 0)} />
          </Col>
          <Col xs={12} md={4}>
            <div style={{ color: '#8c8c8c', fontSize: 12, marginBottom: 4 }}>退货率（%）</div>
            <InputNumber min={0} max={100} precision={1} style={{ width: '100%' }} value={returnRate} onChange={v => setReturnRate(v ?? 0)} />
            <div style={{ color: '#bfbfbf', fontSize: 11, marginTop: 2 }}>退货损失 = (售价 − 货品成本) × 退货率</div>
          </Col>
          <Col xs={12} md={4}>
            <div style={{ color: '#8c8c8c', fontSize: 12, marginBottom: 4 }}>仅退款率（%）</div>
            <InputNumber min={0} max={100} precision={1} style={{ width: '100%' }} value={onlyRefundRate} onChange={v => setOnlyRefundRate(v ?? 0)} />
          </Col>
          <Col xs={12} md={4}>
            <div style={{ color: '#8c8c8c', fontSize: 12, marginBottom: 4 }}>出价模式</div>
            <Segmented
              block
              value={bidMode}
              onChange={v => setBidMode(v)}
              options={[{ label: '成交出价', value: 'cpa' }, { label: '投产比出价', value: 'roi' }]}
            />
          </Col>
          <Col xs={12} md={4}>
            {bidMode === 'roi' ? (
              <>
                <div style={{ color: '#8c8c8c', fontSize: 12, marginBottom: 4 }}>目标投产比</div>
                <InputNumber min={0} precision={2} style={{ width: '100%' }} value={roiBid} onChange={v => setRoiBid(v ?? 0)} />
                <div style={{ color: '#bfbfbf', fontSize: 11, marginTop: 2 }}>单笔推广费 = 售价 ÷ 目标投产比</div>
              </>
            ) : (
              <>
                <div style={{ color: '#8c8c8c', fontSize: 12, marginBottom: 4 }}>出价（元/单）</div>
                <InputNumber min={0} precision={2} style={{ width: '100%' }} value={bid} onChange={v => setBid(v ?? 0)} />
                <div style={{ color: '#bfbfbf', fontSize: 11, marginTop: 2 }}>每笔成交固定扣费（最低 4 元）</div>
              </>
            )}
          </Col>
        </Row>
      </Card>

      {/* 地区占比 */}
      <Card title="② 各地区订单占比（%）（合计需 = 100%）" size="small" style={{ marginBottom: 12 }}>
        <Row gutter={[16, 8]} align="middle">
          {ZONES.map(z => (
            <Col xs={8} md={4} key={z}>
              <div style={{ color: '#8c8c8c', fontSize: 12, marginBottom: 4 }}><Tag color={zoneTagColor(z)}>{z}</Tag>订单占比</div>
              <InputNumber min={0} max={100} precision={0} style={{ width: '100%' }} value={zoneRatios[z] ?? null}
                onChange={v => setZoneRatios(prev => ({ ...prev, [z]: v ?? 0 }))} addonAfter="%" />
            </Col>
          ))}
          <Col xs={24} md={8}>
            <Tag color={zoneTotal === 100 ? 'green' : 'red'} style={{ fontSize: 13, padding: '4px 10px' }}>
              合计 {zoneTotal}%{zoneTotal === 100 ? '' : '（需等于 100%）'}
            </Tag>
          </Col>
        </Row>
      </Card>

      {/* 规格列表 */}
      <Card
        title="③ 规格列表（销量占比合计需 = 100%）"
        size="small"
        style={{ marginBottom: 12 }}
        extra={<Button icon={<PlusOutlined />} size="small" onClick={addSpec}>添加规格</Button>}
      >
        <Table rowKey="key" columns={specColumns} dataSource={specs} size="small" pagination={false} scroll={{ x: 860 }} />
        <div style={{ marginTop: 8 }}>
          <Tag color={specTotal === 100 ? 'green' : 'red'} style={{ fontSize: 13, padding: '4px 10px' }}>
            销量占比合计 {specTotal}%{specTotal === 100 ? '' : '（需等于 100%）'}
          </Tag>
        </div>
      </Card>

      {/* 计算结果 */}
      <Card title="④ 计算结果" size="small" style={{ marginBottom: 12 }}>
        <Row gutter={16} align="middle" style={{ marginBottom: 12 }}>
          <Col xs={24} md={10}>
            <div style={{ border: '1px solid var(--brand-border)', borderRadius: 8, padding: 16, textAlign: 'center', background: profitable ? '#f6ffed' : '#fff2f0' }}>
              <div style={{ color: '#8c8c8c', fontSize: 12 }}>综合毛利率</div>
              <div style={{ fontSize: 32, fontWeight: 700, color: profitable ? '#3F8600' : '#CF1322', lineHeight: 1.2 }}>
                {(calc.overall * 100).toFixed(2)}%
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: profitable ? '#3F8600' : '#CF1322', marginTop: 4 }}>
                {profitable ? '✅ 预计盈利' : '❌ 预计亏损'}
              </div>
            </div>
          </Col>
          <Col xs={24} md={14}>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', padding: '8px 4px' }}>
              <div>
                <div style={{ color: '#8c8c8c', fontSize: 12 }}>加权平均单票毛利</div>
                <div style={{ fontSize: 18, fontWeight: 600 }}>¥{yuan(calc.wMargin)}</div>
              </div>
              <div>
                <div style={{ color: '#8c8c8c', fontSize: 12 }}>加权平均售价</div>
                <div style={{ fontSize: 18, fontWeight: 600 }}>¥{yuan(calc.wPrice)}</div>
              </div>
              <div>
                <div style={{ color: '#8c8c8c', fontSize: 12 }}>加权平均运费</div>
                <div style={{ fontSize: 18, fontWeight: 600 }}>¥{yuan(calc.avgFreight)}</div>
              </div>
              <div>
                <div style={{ color: '#8c8c8c', fontSize: 12 }}>每卖 100 单毛利</div>
                <div style={{ fontSize: 18, fontWeight: 600, color: calc.wMargin >= 0 ? '#3F8600' : '#CF1322' }}>¥{yuan(calc.wMargin * 100)}</div>
              </div>
              <div>
                <div style={{ color: '#8c8c8c', fontSize: 12 }}>每单推广费（加权）</div>
                <div style={{ fontSize: 18, fontWeight: 600 }}>¥{yuan(calc.avgAd)}</div>
              </div>
              <div>
                <div style={{ color: '#8c8c8c', fontSize: 12 }}>{calc.isRoi ? '付费占比（1÷目标投产比）' : '付费占比（出价÷客单价）'}</div>
                <div style={{ fontSize: 18, fontWeight: 600 }}>{(calc.bidRatio * 100).toFixed(2)}%</div>
              </div>
              <div>
                <div style={{ color: '#8c8c8c', fontSize: 12 }}>保本投产比（含退货·精确版）</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--brand)' }}>{calc.breakEvenROI != null ? calc.breakEvenROI.toFixed(2) : '-'}</div>
              </div>
              <div>
                <div style={{ color: '#8c8c8c', fontSize: 12 }}>综合毛利（每单）</div>
                <div style={{ fontSize: 18, fontWeight: 600, color: calc.wMargin >= 0 ? '#3F8600' : '#CF1322' }}>¥{yuan(calc.wMargin)}</div>
              </div>
              <div>
                <div style={{ color: '#8c8c8c', fontSize: 12 }}>{calc.isRoi ? '目标投产比' : '当前出价投产比'}</div>
                <div style={{ fontSize: 18, fontWeight: 600 }}>{calc.currentROI != null ? calc.currentROI.toFixed(2) : '-'}</div>
              </div>
              <div>
                <div style={{ color: '#8c8c8c', fontSize: 12 }}>投产比盈亏</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: calc.currentROI != null && calc.breakEvenROI != null ? (calc.currentROI >= calc.breakEvenROI ? '#3F8600' : '#CF1322') : '#8c8c8c' }}>
                  {calc.currentROI != null && calc.breakEvenROI != null ? (calc.currentROI >= calc.breakEvenROI ? '✅ 盈利' : '❌ 亏损') : '-'}
                </div>
              </div>
            </div>
            <Divider style={{ margin: '8px 0' }} />
            <div style={{ color: '#8c8c8c', fontSize: 12 }}>
              单票毛利 = 售价 − 货品成本 − 运费 − 辅材 − 技术服务费(净额) − 退货损失((售价−货品成本)×退货率) − 仅退款损失(售价×仅退款率) − 推广费。推广费口径：成交出价 = 每笔固定出价；投产比出价 = 售价 ÷ 目标投产比（付费占比 = 1 ÷ 目标投产比）。综合毛利率 = Σ(单票毛利 × 规格占比 × 地区占比) ÷ Σ(售价 × 规格占比 × 地区占比)。保本投产比 = 1 ÷ 净毛利率(不含推广费)，已含退货损失与仅退款损失摊销，实际投产比高于该值才盈利。
            </div>
          </Col>
        </Row>
        <Table rowKey="key" columns={detailColumns} dataSource={calc.detail} size="small" pagination={false} scroll={{ x: 850 }} />
      </Card>

      {/* 已保存列表 */}
      <Card title="已保存的商品（点击 👁 查看明细，或加载到上方复用）" size="small">
        <Table rowKey="id" columns={savedColumns} dataSource={saved} size="small" pagination={saved.length > 10 ? { pageSize: 10 } : false}
          locale={{ emptyText: '暂无保存记录，填好上方数据后点「保存该商品」' }} />
      </Card>

      {/* 详情 Modal：点击 👁 后弹出，展示商品参数 + 规格 + 完整计算明细 */}
      <Modal
        title={detailData ? `${detailData.name || '(未命名)'} · 计算明细` : '计算明细'}
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        width={920}
        footer={[
          <Button key="load" onClick={() => {
            const d = detailData; if (!d) return;
            setName(d.name || '');
            setPerSetCost(d.perSetCost ?? 0.148);
            setOtherCost(d.otherCost ?? 0.5);
            setBid(d.bid ?? 4);
            setRoiBid(d.roiBid ?? 5);
            setBidMode(d.bidMode === 'roi' ? 'roi' : 'cpa');
            setServiceRate((d.serviceRate ?? 0.007) * 100);
            setReturnRate((d.returnRate ?? 0.04) * 100);
            setOnlyRefundRate((d.onlyRefundRate ?? 0.02) * 100);
            const zr = {}; ZONES.forEach(z => { zr[z] = (d.zoneRatios?.[z] ?? 0) * 100; });
            setZoneRatios(zr);
            setSpecs((d.specs || []).map((s, i) => ({
              key: i + 1, qty: s.qty, price: s.price,
              ratio: (s.ratio ?? 0) * 100,
              freight: { '一区': s.freight?.['一区'] ?? 0, '二区': s.freight?.['二区'] ?? 0, '三区': s.freight?.['三区'] ?? 0 },
            })));
            message.success('已加载到上方，可修改后重新保存');
            setDetailOpen(false);
          }}>加载到上方表单</Button>,
          <Button key="close" type="primary" onClick={() => setDetailOpen(false)}>关闭</Button>,
        ]}
      >
        {detailData && detailComputed ? (() => {
          const d = detailData;
          const profitable = detailComputed.overall > 0;
          return (
            <>
              <Descriptions column={3} size="small" bordered style={{ marginBottom: 12 }}>
                <Descriptions.Item label="商品名称" span={3}><b>{d.name}</b></Descriptions.Item>
                <Descriptions.Item label="综合毛利率" span={3}>
                  <span style={{ fontSize: 16, fontWeight: 700, color: profitable ? '#3F8600' : '#CF1322' }}>
                    {smart(detailComputed.overall * 100)}% {profitable ? '✅ 预计盈利' : '❌ 预计亏损'}
                  </span>
                </Descriptions.Item>
                <Descriptions.Item label="每套成本">¥{smart(d.perSetCost ?? 0.148)}</Descriptions.Item>
                <Descriptions.Item label="其他直接成本">¥{smart(d.otherCost ?? 0.5)}</Descriptions.Item>
                <Descriptions.Item label="技术服务费率">{smart((d.serviceRate ?? 0.007) * 100)}%</Descriptions.Item>
                <Descriptions.Item label="退货率">{smart((d.returnRate ?? 0.04) * 100)}%</Descriptions.Item>
                <Descriptions.Item label="仅退款率">{smart((d.onlyRefundRate ?? 0.02) * 100)}%</Descriptions.Item>
                <Descriptions.Item label={detailComputed.isRoi ? '目标投产比' : '出价'}>
                  {detailComputed.isRoi ? smart(d.roiBid ?? 5) : '¥' + smart(d.bid ?? 4)}
                </Descriptions.Item>
                <Descriptions.Item label="每单推广费（加权）">¥{smart(detailComputed.avgAd)}</Descriptions.Item>
                <Descriptions.Item label="地区占比" span={3}>
                  {ZONES.map(z => <Tag key={z} color={zoneTagColor(z)}>{z} {((d.zoneRatios?.[z] ?? 0) * 100).toFixed(0)}%</Tag>)}
                </Descriptions.Item>
                <Descriptions.Item label="加权单票毛利">¥{smart(detailComputed.wMargin)}</Descriptions.Item>
                <Descriptions.Item label="加权平均售价">¥{smart(detailComputed.wPrice)}</Descriptions.Item>
                <Descriptions.Item label="加权平均运费">¥{smart(detailComputed.avgFreight)}</Descriptions.Item>
                <Descriptions.Item label="每卖 100 单毛利">
                  <b style={{ color: detailComputed.wMargin >= 0 ? '#3F8600' : '#CF1322' }}>¥{smart(detailComputed.wMargin * 100)}</b>
                </Descriptions.Item>
                <Descriptions.Item label="保本投产比">
                  <b style={{ color: 'var(--brand)' }}>{detailComputed.breakEvenROI != null ? smart(detailComputed.breakEvenROI) : '-'}</b>
                </Descriptions.Item>
                <Descriptions.Item label="当前投产比">
                  {detailComputed.currentROI != null ? smart(detailComputed.currentROI) : '-'}
                </Descriptions.Item>
              </Descriptions>
              <div style={{ marginBottom: 8, color: '#8c8c8c', fontSize: 12 }}>📦 规格列表</div>
              <Table
                rowKey="key"
                size="small"
                pagination={false}
                dataSource={(d.specs || []).map((s, i) => ({ key: i, ...s }))}
                columns={[
                  { title: '套数', dataIndex: 'qty', width: 80 },
                  { title: '售价', dataIndex: 'price', width: 90, render: v => '¥' + smart(v) },
                  { title: '销量占比', dataIndex: 'ratio', width: 90, render: v => (v * 100).toFixed(0) + '%' },
                  ...ZONES.map(z => ({
                    title: `${z}运费`, key: `f-${z}`, width: 100, align: 'right',
                    render: (_, s) => '¥' + smart(s.freight?.[z] ?? 0),
                  })),
                ]}
                style={{ marginBottom: 12 }}
              />
              <div style={{ marginBottom: 8, color: '#8c8c8c', fontSize: 12 }}>📊 计算明细（按当前公式实时重算）</div>
              <Table
                rowKey="key"
                size="small"
                pagination={false}
                scroll={{ x: 850 }}
                dataSource={detailComputed.detail}
                columns={[
                  { title: '规格', dataIndex: 'spec', width: 90 },
                  { title: '地区', dataIndex: 'zone', width: 70, render: v => <Tag color={zoneTagColor(v)}>{v}</Tag> },
                  { title: '售价', dataIndex: 'price', width: 70, align: 'right', render: v => smart(v) },
                  { title: '成本', dataIndex: 'cost', width: 70, align: 'right', render: v => smart(v) },
                  { title: '运费', dataIndex: 'freight', width: 70, align: 'right', render: v => smart(v) },
                  { title: '服务费', dataIndex: 'svcFee', width: 70, align: 'right', render: v => smart(v) },
                  { title: '退货损失', dataIndex: 'retLoss', width: 80, align: 'right', render: v => smart(v) },
                  { title: '仅退款损失', dataIndex: 'onlyRefundLoss', width: 90, align: 'right', render: v => smart(v) },
                  { title: '推广费', dataIndex: 'bid', width: 70, align: 'right', render: v => smart(v) },
                  { title: '单票毛利', dataIndex: 'gp', width: 90, align: 'right',
                    render: v => <span style={{ color: v >= 0 ? '#3F8600' : '#CF1322', fontWeight: 600 }}>{v >= 0 ? '+' : ''}{smart(v)}</span> },
                  { title: '毛利率', dataIndex: 'margin', width: 80, align: 'right',
                    render: v => <span style={{ color: v >= 0 ? '#3F8600' : '#CF1322' }}>{smart(v * 100)}%</span> },
                  { title: '权重', dataIndex: 'weight', width: 70, align: 'right', render: v => (v * 100).toFixed(1) + '%' },
                ]}
              />
            </>
          );
        })() : <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>}
      </Modal>
    </div>
  );
}
